// apps/web/src/hooks/use-socket.ts
import { useEffect, useRef } from 'react'
import { useSWRConfig } from 'swr'
import { getSocket } from '../lib/socket-client'
import { useChatStore } from '../stores/chat-store'

export function useSocket(isAuthenticated = false) {
  const { mutate } = useSWRConfig()
  const store = useChatStore()
  const attachedRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) return

    const socket = getSocket()

    if (!socket.connected) {
      socket.connect()
    }

    if (attachedRef.current) return
    attachedRef.current = true

    socket.on('connect', () => store.setConnected(true))
    socket.on('disconnect', () => store.setConnected(false))

    socket.on('message:new', ({ conversation_id }) => {
      void mutate(`/api/admin/conversations/${conversation_id}/messages`)
      void mutate('/api/admin/conversations')
      if (store.activeConversationId !== conversation_id) {
        store.incrementUnread(conversation_id)
      }
    })

    socket.on('unread:update', ({ conversation_id, count }) => {
      store.setUnreadCount(conversation_id, count)
    })

    socket.on('typing:update', ({ conversation_id, users }) => {
      const current = useChatStore.getState().typingUsers[conversation_id] ?? []
      for (const u of current) {
        store.removeTypingUser(conversation_id, u.user_type, u.user_id)
      }
      for (const u of users) {
        store.addTypingUser(conversation_id, u)
      }
    })

    socket.on('user:online', ({ user_type, user_id }) => {
      store.addOnlineUser({ user_type, user_id })
    })

    socket.on('user:offline', ({ user_type, user_id }) => {
      store.removeOnlineUser(user_type, user_id)
    })

    socket.on('conversation:update', () => {
      void mutate('/api/admin/conversations')
    })

    return () => {
      socket.off('connect')
      socket.off('disconnect')
      socket.off('message:new')
      socket.off('unread:update')
      socket.off('typing:update')
      socket.off('user:online')
      socket.off('user:offline')
      socket.off('conversation:update')
      attachedRef.current = false
    }
  }, [mutate, store])
}
